import { Button } from "@/components/ui/baseUi/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { TextAreaInput } from "@/components/ui/forms/textareaInput"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import { word } from "@modules/core/utils/i18n"
import { useCountriesListArray } from "@modules/language/utils/countries"
import { ProfileLabelVisibility } from "@modules/profile/myProfile/components/profileLabelVisibility"
import { type ProfileFormTabProps } from "@modules/profile/myProfile/profileFormikType"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useState } from "react"

export function BiographyForm({ isFormFieldInValid, getFormErrorMessage, formik }: ProfileFormTabProps) {
  const countryOptions = useCountriesListArray()
  const [open, setOpen] = useState(false)

  return (
    <div className="mt-6 flex grow flex-col justify-start gap-4 px-4">
      <Text fontStyle="TM" className="mb-4 self-center">
        {word("profile.form.biography.title")}
      </Text>

      {/* Biography input */}
      <InputContainer>
        <TextAreaInput
          label={word("profile.form.description.label")}
          name="biography"
          isRequiredInput={false}
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("biography")}
          textareaProps={{
            value: formik.getFieldProps("biography").value,
            onChange: (e) => {
              formik.setFieldValue("biography", e.target.value)
            },
            className: "field-sizing-fixed",
            placeholder: word("profile.form.description.label"),
            rows: 8,
          }}
        />
      </InputContainer>

      {/* Job input */}
      <InputContainer>
        <ProfileLabelVisibility
          label={word("profile.form.job.label")}
          name="job"
          isInvalid={isFormFieldInValid("job")}
          displayVisibleToggle
          visibility={formik.getFieldProps("jobVisibility").value}
          onToggleVisibility={() => formik.setFieldValue("jobVisibility", !formik.getFieldProps("jobVisibility").value)}
        />
        <StandardInput
          name="job"
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("job")}
        />
      </InputContainer>

      {/* Nationality input */}
      <InputContainer>
        <ProfileLabelVisibility
          label={word("profile.form.nationality.label")}
          name="nationality"
          isInvalid={isFormFieldInValid("nationality")}
          displayVisibleToggle
          visibility={formik.getFieldProps("nationalityVisibility").value}
          onToggleVisibility={() =>
            formik.setFieldValue("nationalityVisibility", !formik.getFieldProps("nationalityVisibility").value)
          }
        />

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button variant="outline" role="combobox" aria-expanded={open} className="h-11 w-full justify-between">
                {formik.values.nationality
                  ? countryOptions.find((countryOptions) => countryOptions.code === formik.values.nationality)?.label
                  : word("profile.form.nationality.label")}

                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            }
          />
          <PopoverContent className="w-65 border-0 p-0" align="start">
            <Command
              className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
              filter={(value, search) => {
                if (removeDiacritics(value.toLowerCase()).includes(removeDiacritics(search.toLowerCase()))) return 1
                return 0
              }}
            >
              <CommandInput placeholder={word("global.searchCountry")} className="" />
              <CommandList>
                <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                <CommandGroup>
                  {countryOptions.map((countryOption) => (
                    <CommandItem
                      key={countryOption.code}
                      value={countryOption.label}
                      onSelect={(currentValue) => {
                        const foundCountry = countryOptions.find((item) => item.label === currentValue)
                        formik.setFieldValue("nationality", foundCountry?.code)
                        setOpen(false)
                      }}
                      className="cursor-pointer hover:bg-neutral-200"
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          formik.values.nationality === countryOption.code ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {countryOption.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </InputContainer>
    </div>
  )
}
